/**
 * Éditeur d'image produit : recadrage à POIGNÉES + rotation + point blanc à la PIPETTE.
 *
 * Recadrage façon Photos iPhone (react-image-crop) : un cadre avec des poignées
 * aux coins qu'on étire au doigt ou à la souris, l'image ne bouge pas.
 *
 * Point blanc : en mode 🎯, toucher un endroit de la photo qui DEVRAIT être
 * blanc — le serveur donne la couleur exacte du point (les pixels ne sont pas
 * lisibles côté client : le bucket S3 n'expose pas de CORS, un canvas serait
 * « tainted »), et l'aperçu s'applique EN DIRECT via un filtre SVG feColorMatrix
 * en sRGB, le même calcul par canal que le serveur rejouera à l'enregistrement.
 * On teste autant d'endroits qu'on veut, l'aperçu suit à chaque tap.
 *
 * Tout s'applique AU SERVEUR (sharp), sous une NOUVELLE URL à chaque fois — le
 * service worker cache les images produit 7 jours, une URL inchangée
 * resservirait l'ancienne version. La rotation part immédiatement (un
 * aller-retour par quart de tour) : la lib de poignées ne sait pas tourner
 * l'aperçu, et une rotation CSS fausserait les coordonnées du cadre.
 */
import { useRef, useState } from 'react';
import ReactCrop, { type Crop, type PercentCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { type TextZone } from '../../services/camera/localRecognition';

interface ImageEditModalProps {
  imageUrl: string;
  /** Applique rotation immédiate ; renvoie la nouvelle URL de l'image. */
  onRotate: (rotate: number) => Promise<string>;
  /** Pipette : couleur du point (fractions 0-1) + multiplicateurs vers le blanc. */
  /** Aperçu d'un détourage, sans enregistrer — permet de régler le contour en voyant. */
  onCutoutPreview?: (
    margin: number,
    feather: number,
    shadow?: 'none' | 'portee' | 'sol',
    shadowStrength?: number,
  ) => Promise<{ image_base64: string }>;
  /** Repérage des dates et prix imprimés, avec leur position (côté serveur). */
  onTextZones?: () => Promise<{
    zones: Array<{ text: string; x: number; y: number; width: number; height: number; kind: string }>;
  }>;
  onSample: (x: number, y: number) => Promise<{
    color: { r: number; g: number; b: number };
    multipliers: { r: number; g: number; b: number };
  }>;
  /** Applique recadrage, point blanc et/ou gomme, puis le parent recharge la galerie. */
  onApply: (params: {
    crop?: { x: number; y: number; width: number; height: number };
    white_point_at?: { x: number; y: number };
    white_point_strength?: number;
    erase?: Array<{ x: number; y: number; width: number; height: number }>;
    cutout?: boolean;
    cutout_margin?: number;
    cutout_feather?: number;
    cutout_shadow?: 'none' | 'portee' | 'sol';
    cutout_shadow_strength?: number;
  }) => Promise<void>;
  /** Gomme proposée ? NON sur une photo d'ÉTIQUETTE : sa DLC et son n° de lot
   *  sont une pièce de traçabilité bio, pas un défaut à corriger. */
  allowErase?: boolean;
  onClose: () => void;
}

type EraseRect = { x: number; y: number; width: number; height: number };

/** Le serveur en accepte 8 au plus (ArrayMaxSize sur EraseRectDto). */
const MAX_ERASE = 8;

/** Cadre initial quasi plein : les poignées sont visibles tout de suite. */
const INITIAL_CROP: Crop = { unit: '%', x: 2, y: 2, width: 96, height: 96 };

export default function ImageEditModal({ imageUrl, onRotate, onSample, onCutoutPreview, onTextZones, onApply, allowErase = true, onClose }: ImageEditModalProps) {
  const [currentUrl, setCurrentUrl] = useState(imageUrl);
  const [crop, setCrop] = useState<Crop | undefined>(INITIAL_CROP);
  const [percentCrop, setPercentCrop] = useState<PercentCrop | undefined>(INITIAL_CROP as PercentCrop);
  const [whiteMode, setWhiteMode] = useState(false);
  const [whitePick, setWhitePick] = useState<{ x: number; y: number } | null>(null);
  const [whiteMult, setWhiteMult] = useState<{ r: number; g: number; b: number } | null>(null);
  /** Force du point blanc : 1 = le point désigné devient blanc pur ; en dessous,
   *  on interpole vers « ne rien faire ». Même calcul dans l'aperçu et au serveur. */
  const [whiteStrength, setWhiteStrength] = useState(1);
  /** Redressement fin (curseur) : aperçu CSS en direct, gravé au ✓ seulement. */
  const [angle, setAngle] = useState(0);
  /** Gomme : rectangles en fractions de l'image affichée (repère du serveur). */
  const [eraseMode, setEraseMode] = useState(false);
  const [rects, setRects] = useState<EraseRect[]>([]);
  const [drawing, setDrawing] = useState<{ x0: number; y0: number; rect: EraseRect } | null>(null);
  const [busy, setBusy] = useState<false | 'rotate' | 'save' | 'sample' | 'ocr' | 'cutout'>(false);
  /** Zones de texte proposées par l'OCR local (Mac mini) — à confirmer d'un tap. */
  const [zones, setZones] = useState<TextZone[]>([]);
  const [ocrFait, setOcrFait] = useState(false);
  /** Détourage : le sujet est posé sur du blanc. La marge rogne (négatif) ou
   *  élargit (positif) le contour — le seul réglage qui compte vraiment. */
  const [cutout, setCutout] = useState(false);
  const [cutoutMargin, setCutoutMargin] = useState(0);
  /** Fondu du bord, en pixels : 0 = net (air de découpage), 2-3 = fondu dans le blanc. */
  const [cutoutFeather, setCutoutFeather] = useState(2);
  /** Ombre sous le sujet détouré (01/10/2026) : aucune, portée (silhouette
   *  décalée) ou au sol (tache ovale sous le produit), avec son intensité. */
  const [cutoutShadow, setCutoutShadow] = useState<'none' | 'portee' | 'sol'>('none');
  const [cutoutShadowStrength, setCutoutShadowStrength] = useState(50);
  /** Aperçu du détourage (base64). Remplace l'image affichée tant qu'il est actif. */
  const [cutoutPreview, setCutoutPreview] = useState<string | null>(null);
  const cutoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Recalcule l'aperçu, en laissant retomber le curseur : sans cette pause, un
   * glissement enverrait dix demandes dont neuf seraient jetées.
   */
  const demanderApercu = (
    marge: number,
    fondu = cutoutFeather,
    ombre: 'none' | 'portee' | 'sol' = cutoutShadow,
    forceOmbre = cutoutShadowStrength,
  ) => {
    if (!onCutoutPreview) return;
    if (cutoutTimer.current) clearTimeout(cutoutTimer.current);
    cutoutTimer.current = setTimeout(async () => {
      setBusy('cutout');
      setError(null);
      try {
        const r = await onCutoutPreview(marge, fondu, ombre, forceOmbre);
        setCutoutPreview(r.image_base64);
      } catch (e) {
        setCutoutPreview(null);
        setCutout(false);
        setError(
          e instanceof Error && /50\d/.test(e.message)
            ? "Détourage indisponible : le service de retouche n'a pas répondu."
            : e instanceof Error
              ? e.message
              : 'Détourage impossible',
        );
      } finally {
        setBusy(false);
      }
    }, 350);
  };
  const [error, setError] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const eraseLayerRef = useRef<HTMLDivElement>(null);

  const reperer = async () => {
    if (busy || !onTextZones) return;
    setBusy('ocr');
    setError(null);
    try {
      const r = await onTextZones();
      const trouvees: TextZone[] = (r.zones || []).map(z => ({
        text: z.text,
        x: z.x,
        y: z.y,
        width: z.width,
        height: z.height,
        kind: (z.kind === 'date' || z.kind === 'price' ? z.kind : 'other') as TextZone['kind'],
      }));
      setZones(trouvees);
      setOcrFait(true);
      if (trouvees.length === 0) {
        setError('Aucune date ni prix repérés sur cette photo. Tracez la zone à la main.');
      } else {
        setEraseMode(true);
        setWhiteMode(false);
      }
    } catch (e) {
      setError(
        e instanceof Error && /50\d/.test(e.message)
          ? "Repérage indisponible : le service de retouche n'a pas répondu."
          : e instanceof Error
            ? e.message
            : 'Repérage impossible',
      );
    } finally {
      setBusy(false);
    }
  };

  /** Rotation serveur (quart de tour OU angle fin) puis remise à zéro des repères. */
  const applyRotation = async (deg: number) => {
    if (busy) return;
    setBusy('rotate');
    setError(null);
    try {
      const newUrl = await onRotate(deg);
      setCurrentUrl(newUrl);
      setAngle(0);
      setCrop(INITIAL_CROP);
      setPercentCrop(INITIAL_CROP as PercentCrop);
      // Ni le point blanc ni les zones de gomme ne survivent à la rotation :
      // leurs coordonnées désigneraient un autre endroit de la photo.
      setWhitePick(null);
      setWhiteMult(null);
      setRects([]);
      setDrawing(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'La rotation a échoué');
    } finally {
      setBusy(false);
    }
  };

  const handleRotate = () => applyRotation(90);

  /** Pendant le redressement, le cadre de recadrage est masqué : ses
   *  coordonnées ne correspondraient plus à l'image tournée à l'écran. */
  const handleAngleChange = (v: number) => {
    setAngle(v);
    if (v !== 0 && crop) {
      setCrop(undefined);
      setPercentCrop(undefined);
    }
  };

  const resetStraighten = () => {
    setAngle(0);
    setCrop(INITIAL_CROP);
    setPercentCrop(INITIAL_CROP as PercentCrop);
  };

  // Aperçu : rotation CSS + réduction pour garder l'image entière visible
  const straightenTransform = (() => {
    if (angle === 0) return undefined;
    const img = imgRef.current;
    let s = 1;
    if (img?.naturalWidth && img?.naturalHeight) {
      const rad = (Math.abs(angle) * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      s = Math.min(w / (w * cos + h * sin), h / (w * sin + h * cos));
    }
    return `rotate(${angle}deg) scale(${s.toFixed(4)})`;
  })();

  /** Tap en mode pipette : fractions du point sur l'image affichée → échantillon serveur. */
  const handlePickWhite = async (e: React.MouseEvent<HTMLDivElement>) => {
    if (busy || !imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    const fx = (e.clientX - rect.left) / rect.width;
    const fy = (e.clientY - rect.top) / rect.height;
    if (fx < 0 || fx > 1 || fy < 0 || fy > 1) return;
    setBusy('sample');
    setError(null);
    try {
      const sample = await onSample(fx, fy);
      setWhitePick({ x: fx, y: fy });
      setWhiteMult(sample.multipliers);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Échantillonnage impossible');
    } finally {
      setBusy(false);
    }
  };

  /** Position du doigt/curseur en fractions du cadre de l'image. */
  const pointFraction = (e: React.PointerEvent) => {
    const box = eraseLayerRef.current?.getBoundingClientRect();
    if (!box || !box.width || !box.height) return { x: 0, y: 0 };
    return {
      x: Math.min(Math.max((e.clientX - box.left) / box.width, 0), 1),
      y: Math.min(Math.max((e.clientY - box.top) / box.height, 0), 1),
    };
  };

  const startErase = (e: React.PointerEvent<HTMLDivElement>) => {
    if (busy || rects.length >= MAX_ERASE) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = pointFraction(e);
    setDrawing({ x0: p.x, y0: p.y, rect: { x: p.x, y: p.y, width: 0, height: 0 } });
  };

  const moveErase = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drawing) return;
    const p = pointFraction(e);
    setDrawing({
      ...drawing,
      rect: {
        x: Math.min(drawing.x0, p.x),
        y: Math.min(drawing.y0, p.y),
        width: Math.abs(p.x - drawing.x0),
        height: Math.abs(p.y - drawing.y0),
      },
    });
  };

  const endErase = () => {
    if (!drawing) return;
    const r = drawing.rect;
    setDrawing(null);
    // Un simple tap ne doit pas semer des rectangles invisibles
    if (r.width < 0.01 || r.height < 0.01) return;
    setRects(prev => (prev.length >= MAX_ERASE ? prev : [...prev, r]));
  };

  const clearWhite = () => {
    setWhitePick(null);
    setWhiteMult(null);
    setWhiteStrength(1);
  };

  const handleSave = async () => {
    if (busy) return;

    // Cadre quasi plein = pas de recadrage demandé
    const p = percentCrop;
    const isFullFrame = !p || (p.x <= 2.5 && p.y <= 2.5 && p.width >= 95 && p.height >= 95);
    const cropFraction = !isFullFrame && p
      ? {
          x: Math.max(0, p.x / 100),
          y: Math.max(0, p.y / 100),
          width: Math.min(1, p.width / 100),
          height: Math.min(1, p.height / 100),
        }
      : undefined;

    if (!cropFraction && !whitePick && !rects.length && !cutout) {
      // Rotation déjà appliquée au fil de l'eau : rien d'autre à envoyer
      onClose();
      return;
    }

    setBusy('save');
    setError(null);
    try {
      await onApply({
        crop: cropFraction,
        white_point_at: whitePick ?? undefined,
        white_point_strength: whitePick ? whiteStrength : undefined,
        erase: rects.length ? rects : undefined,
        cutout: cutout || undefined,
        cutout_margin: cutout ? cutoutMargin : undefined,
        cutout_feather: cutout ? cutoutFeather : undefined,
        cutout_shadow: cutout && cutoutShadow !== 'none' ? cutoutShadow : undefined,
        cutout_shadow_strength: cutout && cutoutShadow !== 'none' ? cutoutShadowStrength : undefined,
      });
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "L'enregistrement a échoué");
      setBusy(false);
    }
  };

  // Aperçu du point blanc : multiplication par canal, en sRGB comme sharp.linear
  const previewFilter = whiteMult ? 'url(#white-point-preview)' : undefined;
  // Multiplicateurs ADOUCIS — identiques à ceux appliqués côté serveur.
  const softMult = whiteMult && {
    r: 1 + whiteStrength * (whiteMult.r - 1),
    g: 1 + whiteStrength * (whiteMult.g - 1),
    b: 1 + whiteStrength * (whiteMult.b - 1),
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/90 flex flex-col" onClick={e => e.stopPropagation()}>
      {/* Poignées élargies pour le doigt */}
      <style>{`
        @media (pointer: coarse) {
          .ReactCrop__drag-handle { width: 22px; height: 22px; }
        }
        .ReactCrop__drag-handle { background-color: rgba(255,255,255,0.9); border: 1px solid #333; }
      `}</style>

      {/* Filtre d'aperçu : même calcul que sharp .linear([mr,mg,mb]) côté serveur.
          color-interpolation-filters="sRGB" est OBLIGATOIRE : par défaut SVG
          filtre en linearRGB et l'aperçu ne correspondrait pas au résultat. */}
      {whiteMult && (
        <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden>
          <filter id="white-point-preview" colorInterpolationFilters="sRGB">
            <feColorMatrix
              type="matrix"
              values={`${softMult!.r} 0 0 0 0  0 ${softMult!.g} 0 0 0  0 0 ${softMult!.b} 0 0  0 0 0 1 0`}
            />
          </filter>
        </svg>
      )}

      {/* Image + cadre de recadrage (+ calque pipette en mode point blanc) */}
      <div className="flex-1 flex items-center justify-center overflow-hidden p-3 relative">
        {(busy === 'rotate' || busy === 'cutout') && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/50">
            <div className="animate-spin h-10 w-10 border-4 border-white border-t-transparent rounded-full" />
          </div>
        )}
        <div className="relative inline-block">
          <ReactCrop
            crop={crop}
            onChange={(c, pc) => { setCrop(c); setPercentCrop(pc); }}
            keepSelection
            disabled={whiteMode || eraseMode || angle !== 0}
          >
            <img
              ref={imgRef}
              src={cutoutPreview ?? currentUrl}
              alt="Photo à retoucher"
              style={{
                maxWidth: '100%',
                maxHeight: 'calc(100vh - 200px)',
                objectFit: 'contain',
                filter: previewFilter,
                transform: straightenTransform,
              }}
              draggable={false}
            />
          </ReactCrop>
          {whiteMode && (
            <div
              className="absolute inset-0 z-20"
              style={{ cursor: 'crosshair' }}
              onClick={handlePickWhite}
            >
              {whitePick && (
                <span
                  className="absolute w-5 h-5 -ml-2.5 -mt-2.5 rounded-full border-2 border-red-500 bg-white/40 pointer-events-none"
                  style={{ left: `${whitePick.x * 100}%`, top: `${whitePick.y * 100}%` }}
                />
              )}
            </div>
          )}

          {/* Gomme : on trace au doigt comme à la souris (Pointer Events).
              touchAction 'none' — sans lui, iPad fait défiler la page au lieu
              de dessiner. */}
          {eraseMode && (
            <div
              ref={eraseLayerRef}
              className="absolute inset-0 z-20"
              style={{ cursor: 'crosshair', touchAction: 'none' }}
              onPointerDown={startErase}
              onPointerMove={moveErase}
              onPointerUp={endErase}
              onPointerCancel={() => setDrawing(null)}
            />
          )}

          {/* Zones PROPOSÉES par le repérage : en pointillé bleu, tant qu'on ne les
              a pas confirmées. Un tap les fait passer dans les zones à effacer ;
              rien n'est masqué sans ce geste. */}
          {zones.map((z, i) => (
            <button
              key={`z-${i}`}
              type="button"
              onClick={() => {
                if (rects.length >= MAX_ERASE) return;
                setRects(prev => [...prev, { x: z.x, y: z.y, width: z.width, height: z.height }]);
                setZones(prev => prev.filter((_, j) => j !== i));
              }}
              className="absolute z-25 border-2 border-dashed border-sky-400 bg-sky-400/20 hover:bg-sky-400/35"
              style={{
                left: `${z.x * 100}%`,
                top: `${z.y * 100}%`,
                width: `${z.width * 100}%`,
                height: `${z.height * 100}%`,
              }}
              title={`${z.kind === 'date' ? 'Date' : 'Prix'} repéré : « ${z.text} » — toucher pour masquer`}
            >
              <span className="absolute -top-2 left-0 px-1 text-[10px] font-bold rounded bg-sky-500 text-white whitespace-nowrap">
                {z.kind === 'date' ? 'date' : 'prix'}
              </span>
            </button>
          ))}

          {/* Zones à effacer — visibles même hors mode gomme, pour savoir ce
              qu'on s'apprête à enregistrer. Le remplissage définitif prend la
              couleur du pourtour, calculée côté serveur. */}
          {rects.map((r, i) => (
            <div
              key={i}
              className="absolute z-30 border-2 border-dashed border-red-400 bg-white/80 pointer-events-none"
              style={{
                left: `${r.x * 100}%`,
                top: `${r.y * 100}%`,
                width: `${r.width * 100}%`,
                height: `${r.height * 100}%`,
              }}
            >
              {eraseMode && (
                <button
                  type="button"
                  onClick={() => setRects(prev => prev.filter((_, j) => j !== i))}
                  className="absolute -top-3 -right-3 w-7 h-7 rounded-full bg-red-600 text-white text-xs shadow pointer-events-auto"
                  aria-label="Retirer cette zone"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
          {drawing && (
            <div
              className="absolute z-30 border-2 border-dashed border-red-400 bg-white/60 pointer-events-none"
              style={{
                left: `${drawing.rect.x * 100}%`,
                top: `${drawing.rect.y * 100}%`,
                width: `${drawing.rect.width * 100}%`,
                height: `${drawing.rect.height * 100}%`,
              }}
            />
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-600 text-white text-sm text-center py-2 px-4">{error}</div>
      )}

      {/* Force du point blanc : l'aperçu suit le curseur en direct */}
      {whiteMode && whiteMult && (
        <div className="bg-gray-900 px-4 pt-2 flex items-center justify-center gap-2">
          <span className="text-gray-400 text-xs">Force</span>
          <input
            type="range"
            min={10}
            max={100}
            step={5}
            value={Math.round(whiteStrength * 100)}
            onChange={e => setWhiteStrength(parseInt(e.target.value, 10) / 100)}
            disabled={busy !== false}
            className="w-56 max-w-[50vw] accent-white"
            aria-label="Force de la correction du point blanc"
          />
          <span className="text-gray-400 text-xs w-10 tabular-nums">{Math.round(whiteStrength * 100)}%</span>
        </div>
      )}

      {/* Marge du détourage. Le résultat n'est visible qu'après enregistrement :
          le calcul tourne côté serveur et coûte quelques secondes la première
          fois — on ne le relance pas à chaque glissement du curseur. */}
      {cutout && (
        <div className="bg-gray-900 px-4 pt-2 flex items-center justify-center gap-2">
          <span className="text-gray-400 text-xs whitespace-nowrap">Contour</span>
          <input
            type="range"
            min={-15}
            max={15}
            step={1}
            value={cutoutMargin}
            onChange={e => { const v = Number(e.target.value); setCutoutMargin(v); demanderApercu(v, cutoutFeather); }}
            className="w-56 max-w-[50vw]"
          />
          <span className="text-gray-300 text-xs w-24 tabular-nums">
            {cutoutMargin === 0 ? 'tel quel' : cutoutMargin < 0 ? `rogné ${-cutoutMargin} px` : `élargi ${cutoutMargin} px`}
          </span>
        </div>
      )}

      {/* Fondu du bord : un contour net donne au sujet un air de découpage aux
          ciseaux ; deux ou trois pixels le fondent dans le blanc. */}
      {cutout && (
        <div className="bg-gray-900 px-4 pt-2 flex items-center justify-center gap-2">
          <span className="text-gray-400 text-xs whitespace-nowrap">Adoucir</span>
          <input
            type="range"
            min={0}
            max={8}
            step={0.5}
            value={cutoutFeather}
            onChange={e => { const v = Number(e.target.value); setCutoutFeather(v); demanderApercu(cutoutMargin, v); }}
            className="w-56 max-w-[50vw]"
          />
          <span className="text-gray-300 text-xs w-24 tabular-nums">
            {cutoutFeather === 0 ? 'bord net' : `${cutoutFeather} px`}
          </span>
        </div>
      )}

      {/* Ombre sous le produit détouré : aucune / portée / au sol, puis son
          intensité. L'aperçu se recalcule sans relancer le détourage (masque
          gardé en mémoire par le service). */}
      {cutout && (
        <div className="bg-gray-900 px-4 pt-2 flex flex-wrap items-center justify-center gap-2">
          <span className="text-gray-400 text-xs whitespace-nowrap">Ombre</span>
          {([
            ['none', 'Aucune'],
            ['portee', 'Portée'],
            ['sol', 'Au sol'],
          ] as const).map(([val, lib]) => (
            <button
              key={val}
              type="button"
              onClick={() => { setCutoutShadow(val); demanderApercu(cutoutMargin, cutoutFeather, val, cutoutShadowStrength); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium min-h-[36px] ${
                cutoutShadow === val ? 'bg-white text-gray-900' : 'bg-gray-700 text-gray-200'
              }`}
            >
              {lib}
            </button>
          ))}
          {cutoutShadow !== 'none' && (
            <>
              <input
                type="range"
                min={10}
                max={100}
                step={5}
                value={cutoutShadowStrength}
                onChange={e => {
                  const v = Number(e.target.value);
                  setCutoutShadowStrength(v);
                  demanderApercu(cutoutMargin, cutoutFeather, cutoutShadow, v);
                }}
                className="w-40 max-w-[40vw]"
                aria-label="Intensité de l'ombre"
              />
              <span className="text-gray-300 text-xs w-12 tabular-nums">{cutoutShadowStrength} %</span>
            </>
          )}
        </div>
      )}

      {/* Curseur de redressement — aperçu en direct, gravé au ✓ */}
      {!whiteMode && !eraseMode && (
        <div className="bg-gray-900 px-4 pt-2 flex items-center justify-center gap-2">
          <span className="text-gray-400 text-xs w-12 text-right tabular-nums">{angle.toFixed(1)}°</span>
          <input
            type="range"
            min={-45}
            max={45}
            step={0.5}
            value={angle}
            onChange={e => handleAngleChange(parseFloat(e.target.value))}
            disabled={busy !== false}
            className="w-56 max-w-[50vw] accent-white"
            aria-label="Redresser la photo"
          />
          {angle !== 0 && (
            <>
              <button
                type="button"
                onClick={() => applyRotation(angle)}
                disabled={busy !== false}
                className="px-3 py-1.5 min-h-[36px] bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 disabled:bg-gray-600"
              >
                ✓ Redresser
              </button>
              <button
                type="button"
                onClick={resetStraighten}
                disabled={busy !== false}
                className="px-2.5 py-1.5 min-h-[36px] text-gray-300 text-xs rounded-lg border border-gray-600 hover:bg-gray-800"
              >
                ✕
              </button>
            </>
          )}
        </div>
      )}

      <div className="bg-gray-900 px-3 py-2 text-center text-xs text-gray-400 flex items-center justify-center gap-3">
        {whiteMode ? (
          <>
            {busy === 'sample'
              ? 'Analyse du point…'
              : whiteMult
                ? 'Trop marqué ? Baissez la force — l\'aperçu suit. Touchez un autre endroit pour changer de référence.'
                : 'Touchez un endroit qui devrait être BLANC — testez plusieurs endroits, l\'aperçu suit.'}
            {whiteMult && (
              <button type="button" onClick={clearWhite} className="underline text-gray-300">
                Retirer la correction
              </button>
            )}
          </>
        ) : eraseMode ? (
          <>
            {rects.length >= MAX_ERASE
              ? `Maximum atteint (${MAX_ERASE} zones).`
              : 'Tracez un cadre sur la date à effacer — elle sera remplie avec la couleur autour.'}
            {rects.length > 0 && (
              <button type="button" onClick={() => setRects([])} className="underline text-gray-300">
                Tout retirer ({rects.length})
              </button>
            )}
          </>
        ) : angle !== 0 ? (
          'Glissez le curseur pour redresser, puis ✓ Redresser pour appliquer.'
        ) : (
          'Étirez le cadre par ses poignées, puis Enregistrer. Le curseur redresse une photo penchée.'
        )}
      </div>

      {/* Barre d'actions — boutons ≥ 44 px pour le doigt */}
      <div className="bg-gray-900 px-3 pb-3 pt-1 flex items-center justify-center flex-wrap gap-2 safe-area-bottom">
        <button
          type="button"
          onClick={onClose}
          disabled={busy !== false}
          className="px-4 py-3 min-h-[44px] text-gray-300 text-sm rounded-lg border border-gray-600 hover:bg-gray-800"
        >
          Annuler
        </button>
        <button
          type="button"
          onClick={handleRotate}
          disabled={busy !== false || angle !== 0}
          className="px-4 py-3 min-h-[44px] text-white text-sm rounded-lg border border-gray-500 hover:bg-gray-800 flex items-center gap-2 disabled:opacity-50"
        >
          <span className="text-lg leading-none">↻</span> Pivoter
        </button>
        <button
          type="button"
          onClick={() => { setWhiteMode(m => !m); setEraseMode(false); }}
          disabled={busy !== false || angle !== 0}
          title="Désigner un point de la photo qui doit être blanc"
          className={`px-4 py-3 min-h-[44px] text-sm rounded-lg border flex items-center gap-2 disabled:opacity-50 ${
            whiteMode
              ? 'bg-white text-gray-900 border-white font-medium'
              : whiteMult
                ? 'bg-gray-700 text-white border-gray-400'
                : 'text-white border-gray-500 hover:bg-gray-800'
          }`}
        >
          🎯 Point blanc {whiteMult ? '✓' : ''}
        </button>
        {allowErase && (
          <button
            type="button"
            onClick={reperer}
            disabled={busy !== false || angle !== 0 || !onTextZones}
            title="Cherche les dates et les prix imprimés sur la photo et propose les zones à masquer."
            className="px-4 py-3 min-h-[44px] text-sm rounded-lg border text-white border-gray-500 hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
          >
            {busy === 'ocr' && <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />}
            {busy === 'ocr' ? 'Repérage…' : ocrFait ? '🔍 Repérer à nouveau' : '🔍 Repérer les dates'}
          </button>
        )}
        {allowErase && (
          <button
            type="button"
            onClick={() => { setEraseMode(m => !m); setWhiteMode(false); }}
            disabled={busy !== false || angle !== 0}
            title="Effacer une zone de la photo — par exemple une DLC imprimée sur l'emballage"
            className={`px-4 py-3 min-h-[44px] text-sm rounded-lg border flex items-center gap-2 disabled:opacity-50 ${
              eraseMode
                ? 'bg-white text-gray-900 border-white font-medium'
                : rects.length
                  ? 'bg-gray-700 text-white border-gray-400'
                  : 'text-white border-gray-500 hover:bg-gray-800'
            }`}
          >
            🩹 Effacer {rects.length ? `(${rects.length})` : ''}
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            const actif = !cutout;
            setCutout(actif);
            setWhiteMode(false);
            setEraseMode(false);
            if (actif) demanderApercu(cutoutMargin);
            else setCutoutPreview(null);
          }}
          disabled={busy !== false || angle !== 0}
          title="Détache le produit de son fond et le pose sur du blanc. Le premier détourage après une longue pause demande une minute (chargement), les suivants quelques secondes."
          className={`px-4 py-3 min-h-[44px] text-sm rounded-lg border flex items-center gap-2 disabled:opacity-50 ${
            cutout
              ? 'bg-white text-gray-900 border-white font-medium'
              : 'text-white border-gray-500 hover:bg-gray-800'
          }`}
        >
          ✂️ Détourer{cutout && cutoutMargin !== 0 ? ` (${cutoutMargin > 0 ? '+' : ''}${cutoutMargin})` : ''}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={busy !== false || angle !== 0}
          className="px-5 py-3 min-h-[44px] bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:bg-gray-600 flex items-center gap-2"
        >
          {busy === 'save' && <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />}
          {busy === 'save' ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
}
